// Fetch wrapper for the StrayCatBlues SPA — Nexus gateway-native auth.
//
//  • JWT (access) + refresh token in sessionStorage.
//  • callFn(action, payload) → POST /v1/app/straycatblues/fn/straycatblues_api
//    with {action, payload}; unwraps the dispatcher { success, message, data }
//    envelope.
//  • Google OAuth is delegated to the gateway as a service: the browser
//    navigates to /auth/oauth/google?redirect_uri=…&app_id=straycatblues; the
//    gateway handles the provider round-trip and redirects back with tokens in
//    the URL fragment, which consumeOAuthFragment() reads.

import { APP_ID, GATEWAY_BASE, appFnUrl } from './config';

const TOKEN_KEY = 'scb.jwt';
const REFRESH_KEY = 'scb.refresh';

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export function getToken(): string | null { return sessionStorage.getItem(TOKEN_KEY); }
export function setToken(t: string | null): void {
  if (t) sessionStorage.setItem(TOKEN_KEY, t);
  else { sessionStorage.removeItem(TOKEN_KEY); sessionStorage.removeItem(REFRESH_KEY); }
}
function getRefresh(): string | null { return sessionStorage.getItem(REFRESH_KEY); }
function setRefresh(t: string | null): void {
  if (t) sessionStorage.setItem(REFRESH_KEY, t); else sessionStorage.removeItem(REFRESH_KEY);
}
export function isAuthed(): boolean { return !!getToken(); }

// ---- silent refresh (single-flight) ----
let refreshInFlight: Promise<boolean> | null = null;
async function doRefresh(): Promise<boolean> {
  const rt = getRefresh();
  if (!rt) return false;
  try {
    const res = await window.fetch(`${GATEWAY_BASE}/auth/refresh`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });
    if (!res.ok) { setToken(null); return false; }
    const data = (await res.json().catch(() => null)) as { accessToken?: string; refreshToken?: string } | null;
    if (!data?.accessToken) { setToken(null); return false; }
    setToken(data.accessToken);
    if (data.refreshToken) setRefresh(data.refreshToken);
    return true;
  } catch { return false; }
}
function refreshAccess(): Promise<boolean> {
  if (!refreshInFlight) refreshInFlight = doRefresh().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

async function readJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return undefined as unknown as T;
  try { return JSON.parse(text) as T; }
  catch { throw new ApiError(res.status, `Non-JSON response: ${text.slice(0, 160)}`); }
}

interface Envelope<T> { success?: boolean; message?: string; data?: T; }
function unwrap<T>(env: Envelope<T> | T): T {
  if (env && typeof env === 'object' && 'data' in (env as object)) {
    const e = env as Envelope<T>;
    if (e.success === false) throw new ApiError(400, e.message || 'Request failed', e);
    return (e.data as T);
  }
  return env as T;
}

/** Call the app API with one retry across a silent token refresh. */
export async function callFn<T = unknown>(action: string, payload: Record<string, unknown> = {}): Promise<T> {
  const send = async (): Promise<Response> => window.fetch(appFnUrl('straycatblues_api'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
    body: JSON.stringify({ action, payload }),
  });

  let res = await send();
  if (res.status === 401 && getRefresh()) {
    if (await refreshAccess()) res = await send();
  }
  if (res.status === 401) { setToken(null); throw new ApiError(401, 'Your session has expired. Please sign in again.'); }
  if (!res.ok) {
    const body = await readJson<Envelope<unknown>>(res).catch(() => undefined);
    throw new ApiError(res.status, body?.message || `HTTP ${res.status}`, body);
  }
  return unwrap<T>(await readJson<Envelope<T>>(res));
}

// ---- email / password login (fallback for invited volunteers) ----
export async function loginPassword(email: string, password: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/login`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID }),
  });
  const body = await readJson<{ accessToken?: string; refreshToken?: string; message?: string; code?: string }>(res);
  if (body.code === 'PASSWORD_CHANGE_REQUIRED') {
    throw new ApiError(res.status, 'This account must set a new password before its first sign-in. If you have a straycatblues.org Google account, use “Sign in with Google” instead — otherwise contact an admin.');
  }
  if (!res.ok || !body.accessToken) throw new ApiError(res.status, body.message || 'Sign-in failed');
  setToken(body.accessToken);
  if (body.refreshToken) setRefresh(body.refreshToken);
}

export function logout(): void { setToken(null); }

// ---- invite acceptance (register → verify-email → login) ----
export interface InviteInfo { valid: boolean; email?: string; role?: string; }
export async function inviteInfo(token: string): Promise<InviteInfo> {
  const res = await window.fetch(`${GATEWAY_BASE}/v1/app/${APP_ID}/fn/straycatblues_public`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ action: 'invite_info', payload: { token } }),
  });
  if (!res.ok) return { valid: false };
  return unwrap(await readJson<Envelope<InviteInfo>>(res));
}

/** Register a password account for the invited email. Emails a 6-digit code. */
export async function registerInvited(email: string, password: string, displayName?: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/register`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, appId: APP_ID, ...(displayName ? { displayName } : {}) }),
  });
  const body: { code?: string; message?: string } = await readJson<{ code?: string; message?: string }>(res).catch(() => ({}));
  if (res.ok) return;                                  // created — code emailed
  if (body.code === 'PENDING_VERIFICATION') return;    // already pending — code re-sent
  if (body.code === 'EMAIL_EXISTS') throw new ApiError(409, 'This email already has an account — use “Sign in” instead.');
  throw new ApiError(res.status, body.message || 'Registration failed');
}

export async function verifyEmail(email: string, code: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/verify-email`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  });
  const body: { message?: string } = await readJson<{ message?: string }>(res).catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.message || 'Could not verify that code.');
}

export async function resendVerification(email: string): Promise<void> {
  await window.fetch(`${GATEWAY_BASE}/auth/resend-verification`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

// ---- password management ----
export async function requestPasswordReset(email: string): Promise<void> {
  await window.fetch(`${GATEWAY_BASE}/auth/forgot-password`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  // Always 200 (no user-enumeration); nothing to throw.
}

export async function resetPassword(email: string, code: string, newPassword: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/reset-password`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code, newPassword }),
  });
  const body: { message?: string } = await readJson<{ message?: string }>(res).catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.message || 'Could not reset password.');
}

export async function changePassword(currentPassword: string, newPassword: string): Promise<void> {
  const res = await window.fetch(`${GATEWAY_BASE}/auth/change-password`, {
    method: 'POST', headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
    body: JSON.stringify({ currentPassword, newPassword }),
  });
  const body: { message?: string } = await readJson<{ message?: string }>(res).catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.message || 'Could not change password.');
}

// ---- file upload via the platform File Service ----
// POST /v1/app/straycatblues/files { filename, contentType, dataBase64 } → { url }.
// Returns the tokenized same-origin URL + metadata to store in items.photo_ref
// and register in the media library.
export interface UploadResult { url: string; filename: string; contentType: string; sizeBytes?: number; }
export async function uploadFile(file: File): Promise<UploadResult> {
  const dataBase64 = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(',')[1] || '');
    r.onerror = () => reject(new Error('Could not read file'));
    r.readAsDataURL(file);
  });
  // Filenames are echoed in a Content-Disposition header on download, which must
  // be Latin-1 — strip non-ASCII (e.g. the U+202F in macOS screenshot names) so
  // serving the file can't 500.
  const safeName = (file.name || 'image').replace(/[^\x20-\x7E]/g, '_');
  const send = () => window.fetch(`${GATEWAY_BASE}/v1/app/${APP_ID}/files`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
    body: JSON.stringify({ filename: safeName, contentType: file.type || 'application/octet-stream', dataBase64 }),
  });
  let res = await send();
  if (res.status === 401 && getRefresh()) { if (await refreshAccess()) res = await send(); }
  const body = await readJson<{ url?: string; sizeBytes?: number; message?: string; error?: string }>(res);
  if (!res.ok || !body.url) throw new ApiError(res.status, body.message || body.error || 'Upload failed');
  return { url: body.url, filename: safeName, contentType: file.type || 'application/octet-stream', sizeBytes: body.sizeBytes };
}

// ---- Google OAuth via the gateway ----
export function startGoogleOAuth(): void {
  const redirectUri = window.location.origin + '/';
  const params = new URLSearchParams({ redirect_uri: redirectUri, app_id: APP_ID });
  window.location.assign(`${GATEWAY_BASE}/auth/oauth/google?${params}`);
}

export interface OAuthResult { ok: boolean; error?: string; }
export function consumeOAuthFragment(): OAuthResult | null {
  const hash = window.location.hash || '';
  if (!hash.startsWith('#') || !hash.includes('oauth_result=')) return null;
  const params = new URLSearchParams(hash.slice(1));
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  if (params.get('oauth_result') === 'error') return { ok: false, error: params.get('oauth_error') || 'Google sign-in failed.' };
  if (params.get('oauth_result') === 'success') {
    const at = params.get('access_token') || undefined;
    const rt = params.get('refresh_token') || undefined;
    if (at) setToken(at);
    if (rt) setRefresh(rt);
    return { ok: !!at };
  }
  return null;
}
